import * as React from 'react';
import { Component } from 'react';
import propz from 'propz';
import { parse } from 'query-string';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Request } from '../../../../models/request';
import { FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { ROLE } from 'Src/consts/user';
import { PERMISSION_REQUEST_STATUS } from '../../../../consts/permissionRequest';
import { DEFAULT_LIMIT } from 'Src/consts/table';
import {
  ColumnDefinition,
  TABLE_SORT_DIRECTION,
  getOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  getFilters2,
  FunctionAccessorColumnDefinition
} from 'Src/helpers/table/table';
import {
  getCreatedAt,
  getRequestSports,
  getRequestRole,
  getRequestStatus,
  getRequestSchoolName,
  getRequestComment
} from '../../../../helpers/accessor/accessor';
import {
  getProfileRequests,
  createProfileRequest,
  deleteProfileRequest,
  getPermissions
} from 'Src/helpers/service/admin/user';
import { Loader } from '../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../components/SimpleModal/SimpleModal';
import { GridSortIcon } from '../../../../components/Grid/GridSortIcon';
import { TextCell } from '../../../../components/Grid/Cells/TextCell';
import { RequestForm } from './RequestForm/RequestForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Role } from 'Src/models/roles';
import { Permission } from 'Src/models/permission';
import { getUserPermissions } from 'Src/helpers/user/user';
import './RequestsTab.scss';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Date',
    field: 'createdAt',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getCreatedAt
  },
  {
    text: 'School',
    field: 'school',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getRequestSchoolName
  },
  {
    text: 'Request',
    field: 'requestPreset',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getRequestRole
  },
  {
    text: 'Details',
    field: 'requestComment',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getRequestComment
  },
  {
    text: 'Activities / Sports',
    field: 'sports',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getRequestSports
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: getRequestStatus
  }
];

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  onRoleChange?: (
    roleName: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    permission: Permission
  ) => Promise<any>;
  availableRoles?: Role[];
}

interface State {
  isLoading: boolean;
  items: Request[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  basePath: string;
  isRequestFormModalOpen: boolean;
  isConfirmationModalOpen: boolean;
  isSuccessModalOpen: boolean;
  requestToDeleteId: string;
  lastRequest?: Request;
}

export class RequestsTab extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      items: [],
      sortDirection: '',
      sortColumnsName: '',
      basePath: '',
      isRequestFormModalOpen: false,
      isConfirmationModalOpen: false,
      isSuccessModalOpen: false,
      requestToDeleteId: undefined,
      lastRequest: undefined
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);
    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order, DEFAULT_LIMIT);

    getProfileRequests(user, serverQueryFilter).then(requests => {
      this.setState({
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        items: requests,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  onSortClick = (event, sortField) => {
    event.preventDefault();

    const { sortDirection, sortColumnsName } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  getTitles = () => {
    return COLUMNS.map((column, index) => {
      const { isSort, text, field } = column;
      const { sortDirection, sortColumnsName } = this.state;

      if (isSort) {
        return (
          <th key={`${text}_sort_${index}`}>
            <a className="eHeadSort" href="" onClick={event => this.onSortClick(event, field)}>
              {`${text} `}
              <GridSortIcon
                tableSortDirection={sortDirection}
                tableSortColumnsName={sortColumnsName}
                tableColumnName={field}
              />
            </a>
          </th>
        );
      } else {
        return <th key={`${text}_sort_${index}`}>{`${text}`}</th>;
      }
    });
  };

  renderDataRow = (dataItem: any, columns: ColumnDefinition[]) => {
    const { user } = this.props;
    const options = { user };
    const rowCells = columns.map(column => {
      const funcAccessorColumn = column as FunctionAccessorColumnDefinition;
      const value = funcAccessorColumn.accessor(dataItem, options);
      const strValue = String(value);
      return <TextCell key={column.field} text={strValue} />;
    });
    return rowCells;
  };

  renderActionCell = (requestItem: Request) => {
    const status = propz.get(requestItem, ['status']);
    const actionCell =
      status === PERMISSION_REQUEST_STATUS.NEW ? (
        <td onClick={() => this.onCancelRequestClick(requestItem.id)} className="eRolesTabLink">
          Cancel
        </td>
      ) : (
        <td></td>
      );

    return actionCell;
  };

  onCloseRequestFormModal = () => {
    this.setState({
      isRequestFormModalOpen: false
    });
  };

  onSubmitRequestForm = data => {
    const { user } = this.props;

    this.setState({ isLoading: true });

    createProfileRequest(user, data).then(request => {
      this.setState({
        isLoading: false,
        isRequestFormModalOpen: false,
        isSuccessModalOpen: true,
        lastRequest: request
      });
    });
  };

  renderRequestFormModal = () => {
    const { user } = this.props;
    const { isRequestFormModalOpen, items } = this.state;

    return (
      <SimpleModal customClass="mSchoolRequestFormWidth" isOpen={isRequestFormModalOpen} onBackdropClick={this.onCloseRequestFormModal}>
        <RequestForm
          user={user}
          onCancel={this.onCloseRequestFormModal}
          onSubmit={this.onSubmitRequestForm}
          requests={items}
        />
      </SimpleModal>
    );
  };

  onAddRequestClick = () => {
    this.setState({
      isRequestFormModalOpen: true
    });
  };

  renderSuccessModal = () => {
    const { isSuccessModalOpen, lastRequest } = this.state;

    if (!isSuccessModalOpen) return null;

    const status = lastRequest?.status;
    const preset = lastRequest?.requestedPermission?.preset;
    const requestedPermissionAny = lastRequest?.requestedPermission as any;
    const schoolName = requestedPermissionAny?.school?.name;

    const isAutoAcceptedAdmin =
      status === PERMISSION_REQUEST_STATUS.ACCEPTED && (preset === ROLE.ADMIN || preset === 'ADMIN');

    const title = isAutoAcceptedAdmin ? (
      <div className="modal-title-container">
        <FontAwesomeIcon icon={faCheckCircle} className="modal-icon success" />
        <span className="modal-title-text">You are now an admin</span>
      </div>
    ) : (
      <div className="modal-title-container">
        <FontAwesomeIcon icon={faCheckCircle} className="modal-icon success" />
        <span className="modal-title-text">Success</span>
      </div>
    );

    const body = isAutoAcceptedAdmin ? (
      <div className="modal-body-text">
        <p>
          Your admin access for {schoolName || 'the school'} has been granted automatically because you are the first
          administrator for this school.
        </p>
        <p>
          To help you get started, we&apos;ve activated a 30-day trial of the Sport, Clubs, Awards &amp; House Points
          and Communications modules for your school. Challenges and Leagues &amp; Competitions are also included for
          FREE with your Sport module.
        </p>
        <p>
          You can now start adding fixtures and clubs, invite colleagues, parents and students, set up awards and
          explore how Squad In Touch works with your real data.
        </p>
        <p>Click &quot;Ok&quot; and you&apos;ll be taken straight to your school dashboard.</p>
      </div>
    ) : (
      <div className="modal-body-text">
        <p>Your role request has been submitted successfully.</p>
        <p>Approval may take some time, and you will receive an email notification once your request is processed.</p>
      </div>
    );

    return (
      <SimpleModal
        isOpen={isSuccessModalOpen}
        title={title}
        body={body}
        buttonText="Ok"
        onButtonClick={this.onCloseSuccessModal}
        customClass="upProfileCompactModal"
      />
    );
  };

  onCloseSuccessModal = () => {
    const { lastRequest } = this.state;
    const { onRoleChange, availableRoles, user, history } = this.props;

    const status = lastRequest?.status;
    const preset = lastRequest?.requestedPermission?.preset as string | undefined;
    const schoolId = (lastRequest?.requestedPermission as any)?.schoolId;

    const isAutoAcceptedAdmin =
      status === PERMISSION_REQUEST_STATUS.ACCEPTED && (preset === ROLE.ADMIN || preset === 'ADMIN');

    this.setState({
      isSuccessModalOpen: false,
      lastRequest: undefined
    });

    if (isAutoAcceptedAdmin && schoolId) {
      const hasExistingRoles = !!availableRoles && getUserPermissions(availableRoles).length > 0;

      if (hasExistingRoles && onRoleChange) {
        const filter = { limit: DEFAULT_LIMIT, skip: 0 };

        getPermissions(user, filter).then((permissions: Permission[]) => {
          const newPermission = permissions.find(
            (p: Permission) => p.preset === (preset as string) && p.school && p.school.id === schoolId
          );

          if (newPermission) {
            onRoleChange(newPermission.preset, newPermission.school.id, newPermission.school.name, newPermission).then(
              () => {
                history.push({ pathname: '/dashboard' });
              }
            );
          } else {
            window.location.href = `/dashboard?schoolId=${schoolId}`;
          }
        });

        return;
      }

      window.location.href = `/dashboard?schoolId=${schoolId}`;
      return;
    }

    this.setItems();
  };


  getRoleLabel = (preset: string): string => {
    if (!preset) return '';
    return preset.charAt(0).toUpperCase() + preset.slice(1).toLowerCase();
  };

  getRoleBadgeClass = (preset: string): string => {
    switch (preset) {
      case ROLE.ADMIN:
        return 'upRoleBadge upRoleBadgeInfo';
      case ROLE.PARENT:
        return 'upRoleBadge upRoleBadgeSuccess';
      case ROLE.STUDENT:
        return 'upRoleBadge upRoleBadgeNeutral';
      default:
        return 'upRoleBadge upRoleBadgeWarning';
    }
  };

  getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case PERMISSION_REQUEST_STATUS.ACCEPTED:
        return 'upProfileStatus upProfileStatusSuccess';
      case PERMISSION_REQUEST_STATUS.REJECTED:
        return 'upProfileStatus upProfileStatusDanger';
      default:
        return 'upProfileStatus upProfileStatusWarning';
    }
  };

  getStatusLabel = (status: string): string => {
    switch (status) {
      case PERMISSION_REQUEST_STATUS.ACCEPTED:
        return 'Accepted';
      case PERMISSION_REQUEST_STATUS.REJECTED:
        return 'Declined';
      default:
        return 'Pending';
    }
  };
  renderConfirmationModal = () => {
    const { isConfirmationModalOpen } = this.state;

    const body = (
      <div className="upRequestCancelModalBody">
        <p>This pending role request has not been reviewed yet.</p>
        <p>If you cancel it, the school will no longer be able to approve it and you will need to submit a new request.</p>
      </div>
    );

    return (
      <SimpleModal
        title={'Cancel request'}
        isOpen={isConfirmationModalOpen}
        body={body}
        buttonText={'Cancel request'}
        onButtonClick={this.onOkConfirmationModalClick}
        buttonCancelText={'Keep request'}
        onCloseClick={this.onCancelConfirmationModalClick}
        onCloseIconClick={this.onCancelConfirmationModalClick}
        customClass="upProfileCompactModal upProfileDangerModal"
        isBodyBold={false}
      />
    );
  };

  onCancelConfirmationModalClick = () => {
    this.setState({
      isConfirmationModalOpen: false
    });
  };

  onCancelRequestClick = (requestId: string) => {
    this.setState({
      isConfirmationModalOpen: true,
      requestToDeleteId: requestId
    });
  };

  onOkConfirmationModalClick = () => {
    const { user } = this.props;
    const { requestToDeleteId } = this.state;

    this.setState({
      isLoading: true
    });

    deleteProfileRequest(user, requestToDeleteId).then(() => {
      const { items } = this.state;
      const itemsUpdated = items.filter(request => request.id !== requestToDeleteId);

      this.setState({
        isConfirmationModalOpen: false,
        items: itemsUpdated,
        isLoading: false
      });
    });
  };

  render() {
    const { isLoading, items, isRequestFormModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isRequestFormModalOpen ? 'modal-open upRequestsTab' : 'upRequestsTab';

    return (
      <div className={classes}>
        {this.renderRequestFormModal()}
        {this.renderSuccessModal()}
        {this.renderConfirmationModal()}

        <div className="upProfileInfo">
          <span className="upProfileInfoIcon">i</span>
          <div>
            <div>Track the status of every role request.</div>
            <div>Pending requests are awaiting approval. Accepted roles are active on your account.</div>
          </div>
        </div>

        <section className="upProfileCard">
          <header className="upProfileCardHeader">
            <div>
              <h2 className="upProfileCardTitle">Profile requests</h2>
              <p className="upProfileCardSub">Cancel a pending request or send a new one at any time.</p>
            </div>
            <button className="upProfileBtn upProfileBtnPrimary" type="button" onClick={this.onAddRequestClick}>
              + Add new request
            </button>
          </header>
          <div className="upProfileCardBody upProfileTableCard">
            <table className="upProfileTable upRequestsTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>School</th>
                  <th>Request</th>
                  <th>Details</th>
                  <th>Activities / Sports</th>
                  <th>Status</th>
                  <th className="upRequestsActionHead">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((requestItem, index) => {
                  const status = propz.get(requestItem, ['status'], '');
                  const preset = propz.get(requestItem, ['requestedPermission', 'preset'], '');
                  const sports = getRequestSports(requestItem, { user: this.props.user });

                  return (
                    <tr key={`profile_requests_tab_${index}`}>
                      <td>{getCreatedAt(requestItem, { user: this.props.user })}</td>
                      <td className="upRequestsSchool">{getRequestSchoolName(requestItem, { user: this.props.user })}</td>
                      <td>
                        <span className={this.getRoleBadgeClass(preset)}>{this.getRoleLabel(preset)}</span>
                      </td>
                      <td className="upRequestsDetails">{getRequestComment(requestItem, { user: this.props.user }) || '-'}</td>
                      <td className="upRequestsSports">{sports || '-'}</td>
                      <td>
                        <span className={this.getStatusBadgeClass(status)}>{this.getStatusLabel(status)}</span>
                      </td>
                      <td>
                        {status === PERMISSION_REQUEST_STATUS.NEW && (
                          <button
                            type="button"
                            className="upProfileActionLink upProfileActionLinkDanger"
                            onClick={() => this.onCancelRequestClick(requestItem.id)}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }
}

